/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useCallback, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { ThemeProvider } from 'styled-components';
import Switch from 'react-switch';
import light from '../../assets/style/themes/light';
import dark from '../../assets/style/themes/dark';
import { Logo, Avata, Footer, Button } from '../../components';
import ContextualGroupCards from '../../sections/ContextualGroupCards';
import GroupSelectors from '../../sections/GroupSelectors';
import UploadSurvey from '../../sections/UploadSurvey';
import SurveysManagement from '../../sections/SurveysManagement';
import { isNullOrEmptyArray } from '../../util/arrays';
import { fetchTenants } from '../../stores/actions/tenants';
import {
  destroySession,
} from '../../stores/actions/access';
import AuthorizationGuard from '../../sections/AuthorizationGuard';
import LgpdSignature from '../../sections/LgpdSignature';

import * as Ui from './styles';
import { ReportManager } from 'src/sections/ReportManager';
import { ReportProvider } from 'src/sections/ReportManager/context/ReportContext';
import ReportModal from 'src/sections/SurveysManagement/ReportModal';
const ErrInvalidAccess = 'Acesso inválido. Faça login novamente!';

function Dashboard(props) {
  // props from redux
  const { dispatch, error, activeSurvey } = props;
  // props to child from redux
  const {
    id,
    name,
    photo,
    token,
    isAdmin,
    isEmployee,
    isCustomer,
    tenants,
    needSignDocuments,
  } = props;
  const childProps = { token, tenants, isAdmin, isEmployee, isCustomer };
  // state
  const [customers, setCustomers] = useState([]);
  const [uploadSurveyModalIsOpen, setUploadSurveyModalIsOpen] = useState(false);
  const [surveysManagementModalIsOpen, setSurveysManagementModalIsOpen] = useState(false);
  const [maximizedCardId, setMaximizedCardId] = useState(null);
  const [isSelectorHidden, setIsSelectorHidden] = useState(false);
  const [isThemeLoading, setIsThemeLoading] = useState(false);
  const [isReportManagerVisible, setIsReportManagerVisible] = useState(false);
  
  // Themes

  // Caso tenha tema ativo e o tema no localStorage seja light o valor do estado sera light.
  const [theme, setTheme] = useState(localStorage.getItem('themeActive') === 'light' ? light : dark);

  // ref
  const reportModalRef = useRef();

  function handleChangeTheme() {
    setIsThemeLoading(true);

    if (theme.title === 'dark') {
      setTheme(light);
      localStorage.setItem('themeActive', 'light');
    } else {
      setTheme(dark);
      localStorage.setItem('themeActive', 'dark');
    }
  }

  useEffect(() => {
    /**
     * Ao trocar o esquema de cores o estado de carregamento
     * é setado para false
     */
     setIsThemeLoading(false);
  }, [theme]);

  // logout
  const logoutCallback = useCallback(() => {
    destroySession(dispatch);
  }, [dispatch]);

  // abre um popup para gerenciamento de pesquisas
  function handleViewSurveysManagement() {
    setSurveysManagementModalIsOpen(true);
  }

  // fecha o popup referente a gerenciamento de pesquisas
  function handleCloseViewSurveysManagement() {
    setSurveysManagementModalIsOpen(false);
  }

  // abre um popup para inclusão de uma nova pesquisa
  function handleNewSurvey() {
    setUploadSurveyModalIsOpen(true);
  }

  // faz logout
  function handleLogout() {
    logoutCallback();
  }

  // fecha o modal de uma pesquisa
  function handleCloseUploadSurveyModal() {
    setUploadSurveyModalIsOpen(false);
  }

  // maximize um determinado card
  function handleMaximizeCard(cardId) {
    setMaximizedCardId(cardId);
  }

  // maximize um determinado card
  function handleMinimizeMaximizedCard() {
    setMaximizedCardId(null);
  }

  // Funcao executada quando se deseja gerar um relatorio
  function handleGenerateReport(reportDataPayload) {
    reportModalRef.current.buildReportPayload(reportDataPayload);
  }

  // Controla a visibilidade da modal do gerenciador de relatórios
  function handleReportManager() {
    setIsReportManagerVisible(true);
  }

  // Controla a visualização do reportManager
  function handleCloseReportManager() {
    setIsReportManagerVisible(false);
  }

  /**
   * Callback executado quando a geracao do payload do relatorio eh concluida
   */
  function onReportDone() {
  }

  // efeito para listagem de clientes após alterar o token
  useEffect(() => {
    // recuperar a lista de clientes se não for um cliente.
    // Enquanto houver LGPD pendente, o token não tem permissão (retornaria 401),
    // então só busca após a assinatura — o efeito re-roda quando a flag cai.
    if (token && !isCustomer && !needSignDocuments) {
      fetchTenants(dispatch);
    }
  }, [dispatch, token, isCustomer, needSignDocuments]);

  // efeito para definição de tenants para um cliente
  useEffect(() => {
    // atribui tentants
    if (!isNullOrEmptyArray(tenants)) {
      setCustomers(tenants);
    } else if (isCustomer && !needSignDocuments) {
      // com LGPD pendente o cliente não alimenta os seletores, que chamariam o
      // analyze-api (403) por trás da modal de assinatura
      setCustomers([{ id, nome: name }]);
    }
  }, [dispatch, tenants, id, name, isCustomer, needSignDocuments]);

  // error effect
  useEffect(() => {
    // Com LGPD pendente, o token não tem permissão e qualquer 401 é esperado
    // (não é erro de credenciais). Não mostra "Acesso inválido" nem desloga —
    // o usuário precisa assinar primeiro.
    if (needSignDocuments) return;
    if (error) {
      const errResp = error.response;
      if (errResp && errResp.status >= 401) {
        toast.error(ErrInvalidAccess);
        setTimeout(() => {
          destroySession(dispatch);
        });
      } else {
        toast.error(error.toString());
      }
    }
  }, [dispatch, error, needSignDocuments]);

  function getSwitch() {
    return (
      <Switch
        checked={theme.title === 'light'}
        onChange={isThemeLoading ? () => {} : () => handleChangeTheme()}
        onColor="#00BEC1"
        onHandleColor="#fff"
        handleDiameter={20}
        uncheckedIcon={false}
        checkedIcon={false}
        boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
        activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
        height={13}
        width={35}
        className="react-switch"
      // id="material-switch"
      />
    );
  }

  /**
   * Recupera um array de itens de menu que sao
   * gerados de acordo com a role do usuario
   */
  function getMenuItens() {
    const novaPesquisaMenu = {
      key: 'abc',
      title: 'Nova Avaliação',
      icon: Ui.PlusFile,
      onClick: handleNewSurvey,
    };

    const gerenciarPesquisaMenu = {
      key: 'def',
      title: 'Gerenciar Avaliações',
      icon: Ui.Shared,
      onClick: handleViewSurveysManagement,
    };

    const alterarTemaMenu = {
      key: 'ghi',
      title: 'Modo light',
      icon: getSwitch,
      onClick: handleChangeTheme,
    };
    const gerarRelatorio = {
      key: 'grr',
      title: 'Gerar relatório',
      icon: Ui.Pdf,
      onClick: handleReportManager,
      disabled: !(activeSurvey.client && activeSurvey.survey),
      tooltip: !(activeSurvey.client && activeSurvey.survey) ? 'Selecione uma avaliação para poder gerar o relatório' : null,
    };
    return isAdmin || isEmployee
      ? [novaPesquisaMenu, gerenciarPesquisaMenu, alterarTemaMenu, gerarRelatorio]
      : [alterarTemaMenu];
  }

  return (
    <ThemeProvider theme={theme}>
      <AuthorizationGuard />
      <LgpdSignature />
      <Ui.PageWrapper className={maximizedCardId ? 'fullscreen' : ''} isAdminEmployee={isAdmin || isEmployee} hideSelector={isSelectorHidden}>
        <Ui.Content className="content">
          <Ui.Header className="header" isFullscreen={maximizedCardId && true} hideSelector={isSelectorHidden}>
            <Ui.FixedHeader isAdminEmployee={isAdmin || isEmployee} hideSelector={isSelectorHidden}>
              <Logo theme={theme} />
              <Button className="hide-menu" onClick={() => setIsSelectorHidden(!isSelectorHidden)}>
                <span>
                  SELECIONE PESQUISA/PERGUNTA
                  <Ui.UpArrow hideSelector={isSelectorHidden} />
                </span>
              </Button>
            </Ui.FixedHeader>
          </Ui.Header>
          <Avata
            theme={theme}
            name={name}
            photo={photo}
            type={isAdmin ? 'admin' : 'employee'}
            menuItems={getMenuItens()}
            onLogout={handleLogout}
          />
          <GroupSelectors
            {...childProps}
            tenants={customers}
            maximizedCard={maximizedCardId}
            onMaximize={handleMaximizeCard}
            onMinimize={handleMinimizeMaximizedCard}
            theme={theme}
          />
          {/* combobox group */}
          <ContextualGroupCards
            {...childProps}
            tenants={customers}
            maximizedCard={maximizedCardId}
            onMaximize={handleMaximizeCard}
            onMinimize={handleMinimizeMaximizedCard}
            theme={theme}
          />
          <ReportModal ref={reportModalRef} onFinish={onReportDone} />
          {isReportManagerVisible && (
            <ReportProvider>
              <ReportManager
                surveyId={activeSurvey.survey.id}
                clientId={activeSurvey.client.id}
                showModal={true}
                onClose={handleCloseReportManager}
                onExport={(preProcessedReportPayload) => handleGenerateReport(preProcessedReportPayload)}
              />
            </ReportProvider>
          )}
          <Footer />
        </Ui.Content>
        {/* modals: upload */}
        {uploadSurveyModalIsOpen && (
          <UploadSurvey
            dispatch={dispatch}
            tenants={customers}
            isOpen={uploadSurveyModalIsOpen}
            onClose={handleCloseUploadSurveyModal}
          />
        )}
        {/* modals: management */}
        {surveysManagementModalIsOpen && (
          <SurveysManagement
            theme={theme}
            isOpen={surveysManagementModalIsOpen}
            onClose={handleCloseViewSurveysManagement}
          />
        )}
      </Ui.PageWrapper>
    </ThemeProvider>
  );
}

// [required]
const mapStateToProps = (state) => {
  const { activeSurvey } = state;
  const accessState = state.access.payload;
  const accessError = state.access.error
    ? state.access.error
    : null;
  const tenants = state.tenants.payload;
  const error = state.tenants.error
    ? state.tenants.error
    : accessError;
  const { needSignDocuments } = state.access;
  return { ...accessState, tenants, error, activeSurvey, needSignDocuments };
};

export default connect(mapStateToProps)(Dashboard);
